import { useEffect, useRef, useState } from "react";

/**
 * TermlyEmbed
 * Renders a Termly-hosted policy (Privacy / Terms / Cookie) inline.
 *
 * Shows a small "Loading policy..." placeholder until Termly injects the
 * iframe. The placeholder disappears once the iframe is visible so users
 * never see a blank page.
 */
export default function TermlyEmbed({ dataId, testid = "termly-embed" }) {
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!dataId) return undefined;

    const SCRIPT_ID = "termly-jssdk";
    const SRC = "https://app.termly.io/embed-policy.min.js";

    // Watch for iframe injection by Termly
    const observer = new MutationObserver(() => {
      const ifr = containerRef.current?.querySelector("iframe");
      if (ifr) {
        setReady(true);
        observer.disconnect();
      }
    });
    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, subtree: true });
    }

    // Inject the script if not already on the page
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (window.Termly && typeof window.Termly.initialize === "function") {
        try { window.Termly.initialize(); } catch { /* ignore */ }
      }
    } else {
      const tjs = document.getElementsByTagName("script")[0];
      const js = document.createElement("script");
      js.id = SCRIPT_ID;
      js.src = SRC;
      js.async = true;
      tjs.parentNode.insertBefore(js, tjs);
    }

    // Safety: stop spinner after 8s even if mutation observer missed it
    const safety = setTimeout(() => setReady(true), 8000);

    return () => {
      observer.disconnect();
      clearTimeout(safety);
    };
  }, [dataId]);

  return (
    <div
      ref={containerRef}
      data-testid={testid}
      className="termly-embed-wrapper text-foreground/85 relative min-h-[400px]"
    >
      {!ready && (
        <div
          data-testid={`${testid}-loading`}
          className="absolute inset-x-0 top-0 flex flex-col items-center justify-center py-16 gap-3 pointer-events-none"
        >
          <div className="text-[10px] tracking-[0.3em] uppercase text-foreground/45 font-mono blink-caret">
            Loading policy
          </div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-foreground/30 font-mono">
            via Termly
          </div>
        </div>
      )}
      {/* eslint-disable-next-line react/no-unknown-property */}
      <div name="termly-embed" data-id={dataId}></div>
    </div>
  );
}
